<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="title-section">
          <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
          <h3 class="modal-title">Promotion Overlap Detected</h3>
        </div>
        <button class="close-btn" @click="closeModal" aria-label="Close modal">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Error Message Warning Box -->
        <div class="alert-box">
          <i class="fa-solid fa-circle-exclamation alert-icon"></i>
          <p class="alert-message">{{ message || 'The selected dealer(s) already have another active promotion of the same type for this item in the same time period.' }}</p>
        </div>

        <div class="table-section">
          <h4 class="section-title">Conflicting Promotions</h4>
          <div class="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Conflicting Dealers</th>
                  <th class="actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="conflict in conflicts" :key="conflict.promotion_id" class="table-row">
                  <td class="font-semibold text-dark">{{ conflict.promotion_code }}</td>
                  <td>{{ conflict.promotion_name }}</td>
                  <td><span class="date-badge">{{ formatDate(conflict.start_date) }}</span></td>
                  <td><span class="date-badge">{{ formatDate(conflict.end_date) }}</span></td>
                  <td>
                    <div class="dealers-list">
                      <span v-for="(dealer, idx) in conflict.dealers" :key="idx" class="dealer-chip">
                        {{ dealer }}
                      </span>
                    </div>
                  </td>
                  <td class="actions-cell">
                    <button class="icon-button" @click="viewPromotion(conflict)">
                      <i class="fa fa-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PromotionConflictModal",
  props: {
    message: {
      type: String,
      default: ""
    },
    conflicts: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  mounted() {
    // Prevent background scrolling when modal is open
    document.body.style.overflow = "hidden";
  },
  beforeDestroy() {
    // Re-enable scrolling when destroyed
    document.body.style.overflow = "auto";
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },
    viewPromotion(conflict) {
      // Close the modal first so it doesn't linger on transition
      this.closeModal();
      
      // Resolve the route and open it in a new browser tab/window
      const routeData = this.$router.resolve({
        path: "/view-promotion",
        query: {
          promoId: conflict.promotion_id,
          isApplied: conflict.promotion_applied ?? false
        }
      });
      window.open(routeData.href, "_blank");
    }
  }
};
</script>

<style scoped>
/* Animating the overlay entrance */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6); /* Modern dark backdrop */
  backdrop-filter: blur(4px); /* Sleek premium glassmorphic feel */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000; /* Ensure it stays on top of everything */
  animation: fadeIn 0.25s ease-out;
}

.modal-container {
  background-color: #ffffff;
  width: 90%;
  max-width: 950px;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  box-sizing: border-box;
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background-color: #fff;
  border-bottom: 1px solid #f1f5f9;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.warning-icon {
  font-size: 1.5rem;
  color: #ea580c; /* Deep warning orange */
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

/* Modal Body */
.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Warning/Alert box style */
.alert-box {
  background-color: #fff7ed; /* Light warning orange */
  border-left: 4px solid #ea580c;
  padding: 16px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
}

.alert-icon {
  font-size: 1.25rem;
  color: #ea580c;
  margin-top: 2px;
  flex-shrink: 0;
}

.alert-message {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #7c2d12;
  font-weight: 500;
}

.section-title {
  margin: 0 0 14px 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* Table styling */
.table-section {
  display: flex;
  flex-direction: column;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.9rem;
}

th {
  background-color: #f8fafc;
  color: #475569;
  font-weight: 600;
  padding: 14px 16px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  vertical-align: middle;
}

.table-row {
  transition: background-color 0.15s ease;
}

.table-row:hover {
  background-color: #f8fafc;
}

.font-semibold {
  font-weight: 600;
}

.text-dark {
  color: #0f172a;
}

.date-badge {
  display: inline-block;
  background-color: #f1f5f9;
  color: #475569;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
}

.dealers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 320px;
}

.dealer-chip {
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  white-space: nowrap;
}

.actions-header, .actions-cell {
  text-align: center;
}

/* Styling for icon button */
.icon-button {
  margin: 0;
  font-size: 15px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 0;
}
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}
.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-eye:active {
  transform: scale(1.1);
}
</style>
